import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ApiResponse, Car, PaginatedResponse, User } from '../interfaces';

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private apiUrl = `${environment.apiUrl}/users`;

  constructor(private http: HttpClient) {}

  /**
   * Get paginated list of users with optional filters
   */
  getUsers(
    page: number = 1,
    limit: number = 10,
    search?: string,
    roleId?: number,
    scope?: string,
    isActive?: boolean | null,
    isVerified?: boolean | null,
    includeChildCompanies?: boolean,
    filterCompanyId?: number | null,
    includeDismissed?: boolean,
  ): Observable<PaginatedResponse<User>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('limit', limit.toString());

    if (search) {
      params = params.set('search', search);
    }

    if (roleId) {
      params = params.set('roleId', roleId.toString());
    }

    if (scope) {
      params = params.set('scope', scope);
    }

    if (isActive !== null && isActive !== undefined) {
      params = params.set('isActive', isActive.toString());
    }

    if (isVerified !== null && isVerified !== undefined) {
      params = params.set('isVerified', isVerified.toString());
    }

    if (includeChildCompanies) {
      params = params.set('includeChildCompanies', 'true');
    }

    if (filterCompanyId !== null && filterCompanyId !== undefined) {
      params = params.set('filterCompanyId', filterCompanyId.toString());
    }

    if (includeDismissed) {
      params = params.set('includeDismissed', 'true');
    }

    return this.http.get<PaginatedResponse<User>>(this.apiUrl, { params });
  }

  /**
   * Get all users in the current company (for chat participant selection)
   */
  getCompanyUsers(): Observable<User[]> {
    return this.http.get<ApiResponse<User[]>>(`${this.apiUrl}/chat/company-users`).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Get user info for chat display (name, avatar)
   */
  getUserForChat(userId: number): Observable<User | null> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/chat/${userId}`).pipe(
      map(response => response.data || null)
    );
  }

  /**
   * Get multiple users by IDs (batch request for chat optimization)
   * Optimizes loading of participant names - one request instead of N
   */
  getUsersByIds(userIds: number[]): Observable<User[]> {
    if (userIds.length === 0) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }

    const params = new HttpParams()
      .set('ids', userIds.join(','));

    return this.http.get<ApiResponse<User[]>>(
      `${this.apiUrl}/batch`,
      { params }
    ).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Get user by ID
   */
  getUser(id: number): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${this.apiUrl}/${id}`);
  }

  getDriverAverageRating(driverId: number, companyId?: number): Observable<ApiResponse<{ average: number; count: number }>> {
    const params = companyId ? `?companyId=${companyId}` : '';
    return this.http.get<ApiResponse<{ average: number; count: number }>>(`${this.apiUrl}/ratings/average/${driverId}${params}`);
  }

  /**
   * Create new user
   */
  createUser(user: Partial<User>): Observable<ApiResponse<User>> {
    return this.http.post<ApiResponse<User>>(this.apiUrl, user);
  }

  /**
   * Update existing user
   */
  updateUser(id: number, user: Partial<User>): Observable<ApiResponse<User>> {
    return this.http.patch<ApiResponse<User>>(`${this.apiUrl}/${id}`, user);
  }

  /**
   * Delete user
   */
  deleteUser(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${id}`);
  }

  /**
   * Activate user
   */
  activateUser(id: number): Observable<ApiResponse<User>> {
    return this.http.patch<ApiResponse<User>>(`${this.apiUrl}/${id}/activate`, {});
  }

  /**
   * Deactivate user
   */
  deactivateUser(id: number): Observable<ApiResponse<User>> {
    return this.http.patch<ApiResponse<User>>(`${this.apiUrl}/${id}/deactivate`, {});
  }

  /**
   * Toggle user active status
   */
  toggleActive(id: number): Observable<ApiResponse<User>> {
    return this.http.patch<ApiResponse<User>>(`${this.apiUrl}/${id}/toggle-active`, {});
  }

  // ===============================
  // User Cars Management Methods
  // ===============================

  /**
   * Get assigned cars for user
   */
  getAssignedCars(userId: number): Observable<ApiResponse<Car[]>> {
    return this.http.get<ApiResponse<Car[]>>(`${this.apiUrl}/${userId}/cars`);
  }

  /**
   * Get available cars for assignment to user
   */
  getAvailableCars(userId: number): Observable<ApiResponse<Car[]>> {
    return this.http.get<ApiResponse<Car[]>>(`${this.apiUrl}/${userId}/cars/available`);
  }

  /**
   * Assign cars to user
   */
  assignCars(userId: number, carIds: number[]): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/${userId}/cars`, { carIds });
  }

  /**
   * Unassign cars from user
   */
  unassignCars(userId: number, carIds: number[]): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.apiUrl}/${userId}/cars`, {
      body: { carIds }
    });
  }

  // ======================
  // PROFILE METHODS
  // ======================

  /**
   * Get current user profile
   */
  getProfile(): Observable<ApiResponse<User>> {
    return this.http.get<ApiResponse<User>>(`${environment.apiUrl}/company/auth/profile`);
  }

  /**
   * Update current user profile
   */
  updateProfile(profileData: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    lang?: string;
  }): Observable<ApiResponse<User>> {
    return this.http.put<ApiResponse<User>>(`${environment.apiUrl}/company/auth/profile`, profileData);
  }

  /**
   * Reset password for a user (company/admin action)
   * Generates temp password, sets mustChangePassword=true, sends email
   */
  resetPassword(userId: number): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/${userId}/reset-password`, {});
  }

  /**
   * Send password reset email to user (company/admin action)
   * Generates temp password, sets mustChangePassword=true, sends email
   */
  sendPasswordResetEmail(userId: number): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/${userId}/reset-password`, {});
  }

  /**
   * Change user password directly (company/admin action)
   * Sets new password and mustChangePassword=false
   */
  changeUserPassword(userId: number, passwordData: { password: string }): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(`${this.apiUrl}/${userId}/change-password`, passwordData);
  }
}
